import { Grid, TextField } from "@mui/material"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next";
import { BayProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Bay  } from "assets/SideBarIcons/bay.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useRef, useState } from "react";
import Loader from "layout/Loader";
import { GetBayByIdResponse } from "subModule/src/services/bay/interface";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import DynamicSelect from "common/Select/Select";
import type { TFunction } from "i18next";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { pickProfileEntityTitle } from "Helpers/pickProfileEntityTitle";
interface FormData { [key: string]: any;}

const BAY_ALLOWED_LIMIT_FULL_EN =
    /^(Error updating entity\s*::\s*)?Allowed bay limit is full\.\s*Maximum\s+(\d+)\s+bays allowed\.?\s*$/i;

function formatBayAllowedLimitErrorMessage(message: string | undefined | null, t: TFunction): string {
    if (message == null || message === '') return '';
    const match = message.trim().match(BAY_ALLOWED_LIMIT_FULL_EN);
    if (match) {
        const prefix = match[1] ? `${t('errorUpdatingEntity')} :: ` : '';
        const max = match[2];
        return `${prefix}${t('allowedBayLimitFull', { max })}`;
    }
    return message;
}

export default function BaysModal() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
        sectionLabelKey: "stations",
        moduleLabelKey: "bays",
        moduleHref: "/bays",
        entityFallbackLabelKey: "title",
        isEdit: !!params?.id,
    });
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const { enqueueSnackbar } = useSnackbar();
    const { register, control, handleSubmit, formState: { errors, dirtyFields, isDirty }, reset } = useForm<FormData>({defaultValues: {}});
    const loggedInUserData = useSelector(selectUserInfo);
    const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
    const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
    const isArabicLanguage = (lang?: string) => {
      const l = (lang || "").toLowerCase();
      return l === "arabic" || l === "ar" || l.includes("arab");
    };
    const localizedBayPlaceholder = (lang?: string) =>
      isArabicLanguage(lang) ? t("phBayNameAr") : t("phBayNameEn");
    const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
    const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(params?.id ? false : true);
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>('');
    const [pageNo, setPageNo] = useState<number>(1);
    const [searchLoading, setSearchLoading] = useState<string | null>(null);
    const { data: stationsData, refetch: stationsRefetch } = useGetStationDropdownService({ pageNumber: pageNo, size: 50, name: dropdownSearchTerm });
    const stations = (stationsData?.pages[0]?.data?.content || []).map((item: { id: number, name: string }) => ({ value: item.id, label: item.name }));
    useEffect(() => {
        stationsRefetch();
    }, []);
    useEffect(() => {
        if(params?.id && stationsData) {
            if(loading) getByID();
        }
    }, [params, stationsData]);
    useEffect(() => {
        if (isDirty && params?.id) setIsBtnEnabled(true);
      }, [isDirty, dirtyFields]);
    const getByID = () => {
        BayProfileService.getBayByID(+(params as any)?.id).then((res: GetBayByIdResponse) => {
            if(res.success) {
                reset({ ...res?.data || {},
                    stationId: res.data?.stationId ? { value: res.data?.stationId, label: res.data?.stationName || res.data?.stationId} : null
                });
                captureLabel(pickProfileEntityTitle(res));
                setLoading(false);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err:any) => { console.error("===", err); });
    }
    const onSubmit = (data: any) => {
        setIsBtnEnabled(false);
        if(params?.id) {
            BayProfileService.updateBay(+(params as any)?.id, {
                ...data,
                stationId: data.stationId.value,
            }).then((response: any) => {
                if(response.success) {
                    captureLabel(data?.title);
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('bay') + ' ' + t('updated') + ' ' + t('successfully')} />, { variant: 'success' });
                    navigate(-1);
                } else {
                    const errText =
                        formatBayAllowedLimitErrorMessage(response?.message, t) || response?.message;
                    enqueueSnackbar(<AlertMessages text={errText} />, { variant: 'error' });
                }
                setIsBtnEnabled(true);
            }).catch((err:any) => { 
                console.error("===", err);
                setIsBtnEnabled(true);
                const raw = err?.data?.message || err?.message || "Something went wrong";
                const errText =
                    typeof raw === 'string'
                        ? formatBayAllowedLimitErrorMessage(raw, t) || raw
                        : "Something went wrong";
                enqueueSnackbar(<AlertMessages text={errText} />, { variant: 'error' });
            });
        } else {
            BayProfileService.createBay({
                ...data, 
                stationId: data.stationId.value,
            }).then((response: any) => {
                if(response.success) {
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('bay') + ' ' + t('created') + ' ' + t('successfully')} />, { variant: 'success' });
                    navigate(-1);
                } else {
                    setIsBtnEnabled(true);
                    const errText =
                        formatBayAllowedLimitErrorMessage(response?.message, t) || response?.message;
                    enqueueSnackbar(<AlertMessages text={errText} />, { variant: 'error' });
                }
            }).catch((err:any) => { 
                console.error("===", err);
                setIsBtnEnabled(true);
                const raw = err?.data?.message || err?.message || "Something went wrong";
                const errText =
                    typeof raw === 'string'
                        ? formatBayAllowedLimitErrorMessage(raw, t) || raw
                        : "Something went wrong";
                enqueueSnackbar(<AlertMessages text={errText} />, { variant: 'error' });
            });
        }
    };
    const handleDropdownActions = (typeName: string, action: 'searchIt' | 'loadNext', search?: string) => {
        if(action === 'searchIt') {
            setPageNo(1);
            setDropdownSearchTerm(search);
            if(search) setSearchLoading(typeName);
        } else {
            setPageNo(pageNo + 1);
            setSearchLoading(typeName);
        }
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            if(typeName === 'stations') stationsRefetch().finally(() => setSearchLoading(null));
        }, 300);
    }
    return (<>
        <ComponentHeaderTwo
            breadcrumbItems={breadcrumbItems}
            showBtn={true}
            showSaveBtn={true}
            onClick={() => navigate(-1)}
            onSubmit={handleSubmit(onSubmit)}
            heading={params?.id ? t('editBay') : t('addBay')}
            icon={<Bay height={28} width={28} />}
            btnText={"back"}
            btnType={"back"}
            isBtnEnabled={IsBtnEnabled}
            showCreateBtn={checkPrivileges("ROLE_BAY_CREATE")}
        />
        {loading ? <Loader /> : <SimpleModal
            visable={true}
            title={params?.id ? "editBay" : "addBay"}
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={ 
                <Grid container component="form" id="form" className="items-baseline" spacing={3} onSubmit={handleSubmit(onSubmit)}>
                    <Grid item xs={12} md={3}>
                        <TextField 
                            {...register("title", { validate: { required: (value) => value.trim() !== '' || t('This field is required.'), } })} 
                            label={`${!primaryLanguage ? t('primary') : ''} ${t('title')} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ''} *`}
                            placeholder={localizedBayPlaceholder(primaryLanguage)}
                            title={(`${!primaryLanguage ? t('primary') : ''} ${t('title')} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ''} *`).replace(/\s+/g, ' ').trim()}
                            fullWidth
                            error={!!errors.title} 
                            helperText={errors.title?.message as string} 
                            InputLabelProps={{ shrink: true }}                                sx={{ '& .MuiOutlinedInput-root': { paddingRight: '0px !important' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField 
                            {...register("secondaryTitle")} 
                            label={`${!secondaryLanguage ? t('secondary') : ''} ${t('title')} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ''}`}
                            placeholder={localizedBayPlaceholder(secondaryLanguage)}
                            title={(`${!secondaryLanguage ? t('secondary') : ''} ${t('title')} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ''}`).replace(/\s+/g, ' ').trim()}
                            fullWidth
                            error={!!errors.secondaryTitle} 
                            helperText={errors.secondaryTitle?.message as string} 
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Controller
                            name="stationId"
                            control={control}
                            defaultValue={null}
                            rules={{ required: t('This field is required.') }}
                            render={({ field }) => (
                                <DynamicSelect
                                    label={t('station') + ' *'}
                                    {...field}
                                    options={stations}
                                    isClearable
                                    isSearchable
                                    defaultPlaceholder="phSelectStation"
                                    onChange={(selectedOption: any) => { field.onChange(selectedOption); }}
                                    error={errors.stationId?.message as string}
                                    isLoading={searchLoading === 'stations'}
                                    onInputChange={(search: string, { action }: { action: string }) => {
                                        if(action === 'input-change' || 
                                            (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                                        ) handleDropdownActions("stations", 'searchIt', search);
                                        else if(action === 'input-blur' || action === 'menu-close') {
                                            if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("stations", 'searchIt', '');
                                            else setDropdownSearchTerm('');
                                            setPageNo(1);
                                        }
                                    }}
                                    onMenuScrollToBottom={() => {if(!(stationsData?.pages[0]?.data as any).last) handleDropdownActions("stations", 'loadNext')}}
                                    page={pageNo}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            }
        />}
    </>)
}